import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";

const STORAGE_KEY = "active-live-call";

export interface ActiveCall {
  sessionId: string;
  roomUrl: string;
  token: string;
  isCoach: boolean;
}

interface ActiveCallContextValue {
  activeCall: ActiveCall | null;
  setActiveCall: (call: ActiveCall) => void;
  clearActiveCall: () => void;
}

const ActiveCallContext = createContext<ActiveCallContextValue | undefined>(undefined);

export function ActiveCallProvider({ children }: { children: ReactNode }) {
  const [activeCall, setActiveCallState] = useState<ActiveCall | null>(null);

  const setActiveCall = useCallback((call: ActiveCall) => {
    setActiveCallState(call);
    if (typeof window !== "undefined") {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(call));
    }
  }, []);

  const clearActiveCall = useCallback(() => {
    setActiveCallState(null);
    if (typeof window !== "undefined") {
      window.sessionStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const persisted = window.sessionStorage.getItem(STORAGE_KEY);
    if (!persisted) return;

    try {
      const parsed = JSON.parse(persisted) as ActiveCall;
      if (parsed?.sessionId && parsed?.roomUrl && parsed?.token) {
        setActiveCallState(parsed);
      } else {
        window.sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      window.sessionStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  useEffect(() => {
    if (!activeCall?.sessionId) return;

    const verifyRoomStillActive = async () => {
      const { data } = await supabase
        .from("session_video_rooms")
        .select("id")
        .eq("session_id", activeCall.sessionId)
        .is("ended_at", null)
        .maybeSingle();

      if (!data) {
        clearActiveCall();
      }
    };

    verifyRoomStillActive();

    const channel = supabase
      .channel(`active-call-${activeCall.sessionId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "session_video_rooms",
          filter: `session_id=eq.${activeCall.sessionId}`,
        },
        (payload) => {
          const row = payload.new as { ended_at: string | null };
          if (row.ended_at) {
            clearActiveCall();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [activeCall?.sessionId, clearActiveCall]);

  const value = useMemo(
    () => ({ activeCall, setActiveCall, clearActiveCall }),
    [activeCall, setActiveCall, clearActiveCall]
  );

  return <ActiveCallContext.Provider value={value}>{children}</ActiveCallContext.Provider>;
}

export function useActiveCall() {
  const context = useContext(ActiveCallContext);
  if (!context) {
    throw new Error("useActiveCall must be used within an ActiveCallProvider");
  }
  return context;
}
